import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../../../common/date.pipe';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { StateToProps } from '../../../store';
import { disuseStore, useDispatcher, useStore } from '../../../store.decorator';
import { ScorecardDataService } from '../../core/data.service';
import { ScorecardGridOptions } from '../../core/scorecard-grid-options.model';
import { Widget, WidgetType } from '../../core/template-definition.model';
import { GridTab } from '../../main-page/grid-tab.model';
import { TranslationKeyMap } from '../../scorecard.i18n';
import {
    CMPS_SCORECARD_INITIAL_GRID_TAB,
    CMPS_SCORECARD_PAGE,
    CMPS_SCORECARD_WIDGET,
} from '../../scorecard.metadata';
import { GridData } from '../../scorecard.model';

import { gridReset, reload, sortPerformanceGrid } from './grid.action';
import scorecardGridReducer from './grid.reducer';
import { GridState } from './grid.state';
import { PerformanceGridComponent } from './performance-grid.component';
import { GridComm, ScoredardBridgeService } from './sc-bridge.service';
import { TeamGridComponent } from './team-grid.component';

// TODO refactor. each grid should be changed to be a WidgetType
// TODO refactor out this component
/** @deprecated */
@Component({
    selector: 'cmps-scorecard-grid',
    templateUrl: './grid.component.html',
})
@TakeUntilDestroy()
export class ScGridComponent
    implements OnInit, OnDestroy, StateToProps<GridState>, GridComm
{
    @ViewChild('performanceGrid')
    private readonly _performanceGrid: PerformanceGridComponent;

    @ViewChild('teamGrid')
    private readonly _teamGridComponent: TeamGridComponent;

    state: GridState;
    activeTab: GridTab;
    _prevData: GridData;
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _GridTab = GridTab;
    /** @private */ _collapsed: boolean;

    /** @private */ get _isLoadingData(): Observable<boolean> {
        return this._loadingData.asObservable();
    }

    private readonly _loadingData: BehaviorSubject<boolean>;

    constructor(
        private readonly _translateService: TranslateService,
        @Inject(CMPS_SCORECARD_WIDGET) /** @private */
        public _widget: Widget<ScorecardGridOptions>,
        @Inject(CMPS_SCORECARD_PAGE) private readonly _page: number,
        @Inject(CMPS_SCORECARD_INITIAL_GRID_TAB)
        private readonly _initialGridTab: () => GridTab,
        private readonly _dataService: ScorecardDataService,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _scBridge: ScoredardBridgeService,
    ) {
        this.activeTab = _initialGridTab();
        this._loadingData = new BehaviorSubject<boolean>(false);
    }

    ngOnInit(): void {
        // TODO revise, refactor. this is temporary in order to separate the grids
        // TODO refactor. each grid should be changed to be a WidgetType
        useStore<GridState>(
            this,
            `scorecard.widgets.grid.page${this._page}`,
            scorecardGridReducer.grid,
        );

        this._dataService
            .observableForWidgetType<GridData>(WidgetType.SCORECARD_GRID)
            .pipe(
                tap((data) => {
                    this._prevData = data;
                    useDispatcher(this, gridReset({}));
                    useDispatcher(
                        this,
                        reload({
                            ...data,
                            options: this._widget.options,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._scBridge.grid.connect(this);
    }

    ngOnDestroy(): void {
        this._scBridge.grid.disconnect();

        disuseStore(this);
    }

    /** @private */ _onPerformanceSortChange(columnId: string): void {
        useDispatcher(this, sortPerformanceGrid({ columnId }));
    }

    onTabChange(tabRefId: GridTab): void {
        this.activeTab = tabRefId;
        if (this.activeTab === GridTab.DIRECT_REPORT_VIEW) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._teamGridComponent && this._teamGridComponent.layout();
        } else {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._performanceGrid && this._performanceGrid.layout();
        }
    }
}
